// Mirrors reporting-hub's PreflightPage Tab 4 "Reporting": PDF preview +
// download, Cashflow Projection, and IC Reporting as 3 sub-tabs. PDF
// preview needs the reporting to be `reported`; Cashflow Projection/IC
// Reporting only need a calculation result (CashflowProjectionShell/
// InvestmentRatioCard handle the pre-Calculate 404 gracefully themselves).
//
// GET .../report/html and .../report/pdf (authenticated) return presigned
// S3 URLs of the rendered files: the HTML preview URL is the iframe's src
// directly (no auth header needed), the PDF URL carries Content-Disposition
// attachment and is downloaded via an anchor click.

import React, { useEffect, useState, useCallback } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { API, apiErrorDetail, downloadUrl } from '../jobs';
import CashflowProjectionShell from '../reporting/CashflowProjectionShell';
import IcReportingShell from '../reporting/IcReportingShell';
import LoadingState from '../../common/LoadingState';
import EmptyState from '../../common/EmptyState';
import errorMessage from '../../../helpers/errorMessage';

const PdfReportingPanel = ({ reportingId, status, setLoading }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const isReported = status && status.stage === 'reported';

  const fetchHtml = useCallback(async () => {
    if (!reportingId || !isReported) return;
    setLoading(true);
    try {
      const res = await axiosWithAuth.get(`${API}/reportings/${reportingId}/report/html`, { params: { scope: 'fund' } });
      setPreviewUrl(res.data.url);
      setError(null);
    } catch (err) {
      console.error('[ReportingTab] html fetch error:', errorMessage(err));
      setError(apiErrorDetail(err, 'Failed to load report preview.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingId, isReported]);

  useEffect(() => {
    fetchHtml();
  }, [fetchHtml]);

  const handleDownloadPdf = async () => {
    setLoading(true);
    try {
      const res = await axiosWithAuth.get(`${API}/reportings/${reportingId}/report/pdf`, { params: { scope: 'fund' } });
      downloadUrl(res.data.url, res.data.filename || `CompartmentReport_${reportingId}.pdf`);
      setError(null);
    } catch (err) {
      console.error('[handleDownloadPdf] error:', errorMessage(err));
      setError(apiErrorDetail(err, 'Download PDF failed.'));
    } finally {
      setLoading(false);
    }
  };

  if (!isReported) {
    return <EmptyState icon="pi-file-pdf" title="Generate the reporting on the Calculation tab first." compact />;
  }

  return (
    <div>
      <div className="flex justify-content-end mb-3">
        <Button label="Download PDF" icon="pi pi-download" onClick={handleDownloadPdf} />
      </div>
      {error && <div className="ex-inline-error mb-3">{error}</div>}
      {previewUrl ? (
        <iframe
          title="Compartment Report preview"
          src={previewUrl}
          className="cr-report-preview"
          style={{ width: '100%', height: '75vh' }}
        />
      ) : (
        !error && <LoadingState label="Loading preview" />
      )}
    </div>
  );
};

const ReportingTab = ({ reportingId, status, setLoading }) => {
  return (
    <div>
      <div className="ex-section-header">
        <h3 className="ex-section-title">Reporting</h3>
      </div>
      <TabView>
        <TabPanel header="PDF Reporting">
          <PdfReportingPanel reportingId={reportingId} status={status} setLoading={setLoading} />
        </TabPanel>
        <TabPanel header="Cashflow Projection">
          <CashflowProjectionShell reportingId={reportingId} setLoading={setLoading} />
        </TabPanel>
        <TabPanel header="IC Reporting">
          <IcReportingShell reportingId={reportingId} />
        </TabPanel>
      </TabView>
    </div>
  );
};

export default ReportingTab;
